interface SBARData {
  situation: string;
  background: string;
  assessment: string;
  recommendation: string;
}

interface SBARCardProps {
  sbar: SBARData;
  timestamp: string;
  recordedBy: string;
}

export default function SBARCard({ sbar, timestamp, recordedBy }: SBARCardProps) {
  return (
    <div className="bg-white rounded-xl shadow-md p-6 border-2 border-peacockTeal-200 mb-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg"
            style={{ background: 'linear-gradient(135deg, var(--color-primary-500) 0%, var(--color-primary-600) 100%)' }}
          >
            <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">SBAR Report</h3>
            <p className="text-sm text-gray-600">{new Date(timestamp).toLocaleString()} • {recordedBy}</p>
          </div>
        </div>
        <span className="badge-low">Auto-Generated</span>
      </div>

      {/* SBAR Sections */}
      <div className="space-y-4">
        {/* Situation */}
        <div className="border-l-4 border-red-400 pl-4 py-2 bg-red-50 rounded-r-lg">
          <h4 className="font-bold text-red-900 mb-2 flex items-center">
            <span className="w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center text-sm font-bold mr-2">S</span>
            Situation
          </h4>
          <p className="text-gray-800">{sbar.situation}</p>
        </div>

        {/* Background */}
        <div className="border-l-4 border-blue-400 pl-4 py-2 bg-blue-50 rounded-r-lg">
          <h4 className="font-bold text-blue-900 mb-2 flex items-center">
            <span className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold mr-2">B</span>
            Background
          </h4>
          <p className="text-gray-800">{sbar.background}</p>
        </div>

        {/* Assessment */}
        <div className="border-l-4 border-yellow-400 pl-4 py-2 bg-yellow-50 rounded-r-lg">
          <h4 className="font-bold text-yellow-900 mb-2 flex items-center">
            <span className="w-8 h-8 rounded-full bg-yellow-500 text-white flex items-center justify-center text-sm font-bold mr-2">A</span>
            Assessment
          </h4>
          <p className="text-gray-800">{sbar.assessment}</p>
        </div>

        {/* Recommendation */}
        <div className="border-l-4 border-green-400 pl-4 py-2 bg-green-50 rounded-r-lg">
          <h4 className="font-bold text-green-900 mb-2 flex items-center">
            <span className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center text-sm font-bold mr-2">R</span>
            Recommendation
          </h4>
          <p className="text-gray-800">{sbar.recommendation}</p>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-6 pt-6 border-t border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <svg className="w-5 h-5 text-peacockTeal-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Time saved: <strong className="text-peacockTeal-600">~4 minutes</strong></span>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-secondary text-sm">
            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
            </svg>
            Copy
          </button>
          <button className="btn btn-primary text-sm">
            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Add to EHR
          </button>
        </div>
      </div>
    </div>
  );
}
